/**
 * Subscription Service
 *
 * Frontend API client for Stripe subscription management.
 * Connects to backend endpoints at /subscriptions/*
 */

import { ApiResponse } from './types/api';
import { apiClient } from './apiClient';

// Types for subscription data

export interface SubscriptionPlanLimits {
  monthly_tokens_limit: number;
  monthly_analyses_limit: number;
  monthly_keywords_limit: number;
  projects_limit: number;
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  description: string;
  price: {
    monthly: number | null;
    annual: number | null;
  };
  limits: SubscriptionPlanLimits;
  features: string[];
  contactSales?: boolean;
}

export interface Subscription {
  id: string;
  user_id: string;
  plan_name: string;
  status: 'active' | 'cancelled' | 'past_due' | 'trialing' | 'incomplete';
  billing_interval: 'month' | 'year' | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  stripe_price_id: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  cancelled_at: string | null;
  monthly_tokens_limit: number;
  monthly_tokens_used: number;
  tokens_available: number;
  monthly_analyses_limit: number;
  monthly_analyses_used: number;
  monthly_keywords_limit: number;
  monthly_keywords_used: number;
  projects_limit: number;
  projects_used: number;
  created_at: string;
  updated_at: string;
}

export interface TokenBalance {
  monthly_limit: number;
  used: number;
  available: number;
  plan: string;
}

export interface UsageStats {
  plan: string;
  status: string;
  billing_interval: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  usage: {
    tokens: {
      used: number;
      limit: number;
      available: number;
      percentage: number;
    };
    projects: {
      used: number;
      limit: number;
      percentage: number;
    };
    analyses: {
      used: number;
      limit: number;
      percentage: number;
    };
  };
}

export interface CheckoutSessionRequest {
  planId: string;
  billingInterval: 'month' | 'year';
  /**
   * Anonymous funnel journey id (sessionStorage `rnk_funnel_jid`). Auto-
   * read by `createCheckoutSession` if not supplied. Forwarded to Stripe
   * as `client_reference_id` so the webhook can attribute the resulting
   * `purchase_completed` event back to the originating journey row.
   */
  journeyId?: string;
}

export interface CheckoutSessionResponse {
  sessionId: string;
  url: string;
}

export interface BillingPortalResponse {
  url: string;
}

export interface TokenPurchaseRequest {
  tokenAmount: 100 | 500 | 1000;
}

export interface GeoCreditPurchaseRequest {
  creditAmount: 100 | 300 | 1000;
}

export interface KeywordCreditPurchaseRequest {
  creditAmount: 100 | 300 | 1000;
}

export interface SerpCreditPurchaseRequest {
  creditAmount: 100 | 300 | 1000;
}

export interface AnalysisTopUpPurchaseRequest {
  analysisAmount: 10 | 25 | 50;
}

export interface KeywordsTrackedTopUpPurchaseRequest {
  keywordsTrackedAmount: 25 | 100 | 250;
}

class SubscriptionService {
  /**
   * Get current user's subscription
   */
  async getSubscription(): Promise<ApiResponse<Subscription>> {
    try {
      return await apiClient.get('/subscriptions');
    } catch (error) {
      console.error('Error fetching subscription:', error);
      return {
        success: false,
        error: 'Failed to fetch subscription',
        message: 'Unable to retrieve subscription information'
      };
    }
  }

  /**
   * Get available subscription plans
   */
  async getPlans(): Promise<ApiResponse<{ plans: SubscriptionPlan[]; stripe_configured: boolean }>> {
    try {
      return await apiClient.get('/subscriptions/plans');
    } catch (error) {
      console.error('Error fetching plans:', error);
      return {
        success: false,
        error: 'Failed to fetch plans',
        message: 'Unable to retrieve subscription plans'
      };
    }
  }

  /**
   * Create a Stripe checkout session for subscription
   * Returns a URL to redirect the user to Stripe checkout
   */
  async createCheckoutSession(data: CheckoutSessionRequest): Promise<ApiResponse<CheckoutSessionResponse>> {
    try {
      // Auto-attach the funnel journey id so the backend can carry it into
      // Stripe (`client_reference_id`) and the webhook can attribute the
      // resulting payment to the journey. Caller-provided values win.
      const payload = { ...data };
      if (!payload.journeyId) {
        try {
          const stored = typeof window !== 'undefined'
            ? window.sessionStorage?.getItem('rnk_funnel_jid')
            : null;
          if (stored) payload.journeyId = stored;
        } catch {
          /* sessionStorage unavailable — proceed without it */
        }
      }
      return await apiClient.post('/subscriptions/checkout', payload);
    } catch (error) {
      console.error('Error creating checkout session:', error);
      // WB-72 round 3: keep the server's own words. apiClient puts the backend's reviewed,
      // customer-safe message (or a written fallback) on error.message and its code on
      // error.code, e.g. 409 PAST_DUE_SUBSCRIPTION_EXISTS: "update your card in Billing".
      const err = error as { message?: unknown; code?: unknown } | null;
      return {
        success: false,
        error: typeof err?.code === 'string' && err.code ? err.code : 'Failed to create checkout session',
        message: typeof err?.message === 'string' && err.message ? err.message : 'Unable to start checkout process',
      };
    }
  }

  /**
   * Create a Stripe billing portal session
   * Returns a URL to redirect the user to Stripe's billing portal
   */
  async createBillingPortal(): Promise<ApiResponse<BillingPortalResponse>> {
    try {
      return await apiClient.post('/subscriptions/portal');
    } catch (error) {
      console.error('Error creating billing portal session:', error);
      return {
        success: false,
        error: 'Failed to create billing portal session',
        message: 'Unable to open billing portal'
      };
    }
  }

  /**
   * Open Stripe billing portal in a new window
   * Convenience method that handles the redirect
   */
  async openBillingPortal(): Promise<boolean> {
    const response = await this.createBillingPortal();
    if (response.success && response.data?.url) {
      window.location.href = response.data.url;
      return true;
    }
    return false;
  }

  /**
   * Cancel subscription at period end
   */
  async cancelSubscription(): Promise<ApiResponse<{ message: string; cancel_at_period_end: boolean }>> {
    try {
      return await apiClient.post('/subscriptions/cancel');
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      return {
        success: false,
        error: 'Failed to cancel subscription',
        message: 'Unable to cancel subscription'
      };
    }
  }

  /**
   * Reactivate a cancelled subscription
   */
  async reactivateSubscription(): Promise<ApiResponse<{ message: string; cancel_at_period_end: boolean }>> {
    try {
      return await apiClient.post('/subscriptions/reactivate');
    } catch (error) {
      console.error('Error reactivating subscription:', error);
      return {
        success: false,
        error: 'Failed to reactivate subscription',
        message: 'Unable to reactivate subscription'
      };
    }
  }

  /**
   * Get current token balance
   */
  async getTokenBalance(): Promise<ApiResponse<TokenBalance>> {
    try {
      return await apiClient.get('/subscriptions/tokens');
    } catch (error) {
      console.error('Error fetching token balance:', error);
      return {
        success: false,
        error: 'Failed to fetch token balance',
        message: 'Unable to retrieve token balance'
      };
    }
  }

  /**
   * Purchase additional tokens
   * Returns a checkout session URL for one-time token purchase
   */
  async purchaseTokens(data: TokenPurchaseRequest): Promise<ApiResponse<CheckoutSessionResponse>> {
    try {
      return await apiClient.post('/subscriptions/tokens', data);
    } catch (error) {
      console.error('Error creating token purchase session:', error);
      return {
        success: false,
        error: 'Failed to create token purchase session',
        message: 'Unable to start token purchase'
      };
    }
  }

  /**
   * Get detailed usage statistics
   */
  async getUsageStats(): Promise<ApiResponse<UsageStats>> {
    try {
      return await apiClient.get('/subscriptions/usage');
    } catch (error) {
      console.error('Error fetching usage stats:', error);
      return {
        success: false,
        error: 'Failed to fetch usage statistics',
        message: 'Unable to retrieve usage statistics'
      };
    }
  }

  /**
   * Change plan for an existing subscriber via stripe.subscriptions.update().
   * Does NOT create a second Stripe subscription.
   * Only works when the user already has an active/trialling subscription.
   */
  async changePlan(planId: string): Promise<ApiResponse<Subscription>> {
    try {
      return await apiClient.post('/subscriptions/change-plan', { planId });
    } catch (error) {
      console.error('Error changing plan:', error);
      return {
        success: false,
        error: 'Failed to change plan',
        message: 'Unable to change subscription plan'
      };
    }
  }

  /**
   * Redirect to Stripe checkout for plan upgrade
   * Convenience method that handles the redirect.
   * Used for NEW subscribers only (no existing Stripe subscription).
   */
  async upgradePlan(
    planId: string,
    billingInterval: 'month' | 'year',
    /** Called with the reason when checkout does not start (WB-72 round 3: the server's message). */
    onRefused?: (message: string, code?: string) => void,
  ): Promise<boolean> {
    const response = await this.createCheckoutSession({ planId, billingInterval });
    if (response.success && response.data?.url) {
      window.location.href = response.data.url;
      return true;
    }
    onRefused?.(response.message || 'Couldn\'t start checkout. Please try again.', response.error);
    return false;
  }

  /**
   * Redirect to Stripe checkout for token purchase
   * Convenience method that handles the redirect
   */
  async buyTokens(tokenAmount: 100 | 500 | 1000): Promise<boolean> {
    const response = await this.purchaseTokens({ tokenAmount });
    if (response.success && response.data?.url) {
      window.location.href = response.data.url;
      return true;
    }
    return false;
  }

  /**
   * Purchase GEO credits
   * Returns a checkout session URL for one-time credit purchase
   */
  async purchaseGeoCredits(data: GeoCreditPurchaseRequest): Promise<ApiResponse<CheckoutSessionResponse>> {
    try {
      return await apiClient.post('/subscriptions/geo-credits', data);
    } catch (error) {
      console.error('Error creating GEO credit purchase session:', error);
      return {
        success: false,
        error: 'Failed to create GEO credit purchase session',
        message: 'Unable to start GEO credit purchase'
      };
    }
  }

  /**
   * Purchase Keyword credits
   * Returns a checkout session URL for one-time credit purchase
   */
  async purchaseKeywordCredits(data: KeywordCreditPurchaseRequest): Promise<ApiResponse<CheckoutSessionResponse>> {
    try {
      return await apiClient.post('/subscriptions/keyword-credits', data);
    } catch (error) {
      console.error('Error creating Keyword credit purchase session:', error);
      return {
        success: false,
        error: 'Failed to create Keyword credit purchase session',
        message: 'Unable to start Keyword credit purchase'
      };
    }
  }

  /**
   * Redirect to Stripe checkout for GEO credit purchase
   * Convenience method that handles the redirect
   */
  async buyGeoCredits(creditAmount: 100 | 300 | 1000): Promise<boolean> {
    const response = await this.purchaseGeoCredits({ creditAmount });
    if (response.success && response.data?.url) {
      window.location.href = response.data.url;
      return true;
    }
    return false;
  }

  /**
   * Redirect to Stripe checkout for Keyword credit purchase
   * Convenience method that handles the redirect
   */
  async buyKeywordCredits(creditAmount: 100 | 300 | 1000): Promise<boolean> {
    const response = await this.purchaseKeywordCredits({ creditAmount });
    if (response.success && response.data?.url) {
      window.location.href = response.data.url;
      return true;
    }
    return false;
  }

  /**
   * Purchase SERP / "Deep Check" credits
   * Returns a checkout session URL for one-time credit purchase
   */
  async purchaseSerpCredits(data: SerpCreditPurchaseRequest): Promise<ApiResponse<CheckoutSessionResponse>> {
    try {
      return await apiClient.post('/subscriptions/serp-credits', data);
    } catch (error) {
      console.error('Error creating Deep Check credit purchase session:', error);
      return {
        success: false,
        error: 'Failed to create Deep Check credit purchase session',
        message: 'Unable to start Deep Check credit purchase'
      };
    }
  }

  /**
   * Redirect to Stripe checkout for SERP / "Deep Check" credit purchase
   * Convenience method that handles the redirect
   */
  async buySerpCredits(creditAmount: 100 | 300 | 1000): Promise<boolean> {
    const response = await this.purchaseSerpCredits({ creditAmount });
    if (response.success && response.data?.url) {
      window.location.href = response.data.url;
      return true;
    }
    return false;
  }

  /**
   * Purchase analysis top-up pack
   * Returns a checkout session URL for one-time analysis top-up purchase
   */
  async purchaseAnalysisTopUp(data: AnalysisTopUpPurchaseRequest): Promise<ApiResponse<CheckoutSessionResponse>> {
    try {
      return await apiClient.post('/subscriptions/analysis-topup', data);
    } catch (error) {
      console.error('Error creating analysis top-up purchase session:', error);
      return {
        success: false,
        error: 'Failed to create analysis top-up purchase session',
        message: 'Unable to start analysis top-up purchase'
      };
    }
  }

  /**
   * Redirect to Stripe checkout for analysis top-up purchase
   * Convenience method that handles the redirect
   */
  async buyAnalysisTopUp(analysisAmount: 10 | 25 | 50): Promise<boolean> {
    const response = await this.purchaseAnalysisTopUp({ analysisAmount });
    if (response.success && response.data?.url) {
      window.location.href = response.data.url;
      return true;
    }
    return false;
  }

  /**
   * Purchase competitor analysis top-up pack
   * Returns a checkout session URL for one-time competitor analysis top-up purchase
   */
  async purchaseCompetitorAnalysisTopUp(data: { competitorAnalysisAmount: 5 | 15 | 30 }): Promise<ApiResponse<CheckoutSessionResponse>> {
    try {
      return await apiClient.post('/subscriptions/competitor-analysis-topup', data);
    } catch (error) {
      console.error('Error creating competitor analysis top-up purchase session:', error);
      return {
        success: false,
        error: 'Failed to create competitor analysis top-up purchase session',
        message: 'Unable to start competitor analysis top-up purchase'
      };
    }
  }

  /**
   * Redirect to Stripe checkout for competitor analysis top-up purchase
   */
  async buyCompetitorAnalysisTopUp(competitorAnalysisAmount: 5 | 15 | 30): Promise<boolean> {
    const response = await this.purchaseCompetitorAnalysisTopUp({ competitorAnalysisAmount });
    if (response.success && response.data?.url) {
      window.location.href = response.data.url;
      return true;
    }
    return false;
  }

  /**
   * Purchase keywords tracked top-up pack
   */
  async purchaseKeywordsTrackedTopUp(data: KeywordsTrackedTopUpPurchaseRequest): Promise<ApiResponse<CheckoutSessionResponse>> {
    try {
      return await apiClient.post('/subscriptions/keywords-tracked-topup', data);
    } catch (error) {
      console.error('Error creating keywords tracked top-up purchase session:', error);
      return {
        success: false,
        error: 'Failed to create keywords tracked top-up purchase session',
        message: 'Unable to start keywords tracked top-up purchase'
      };
    }
  }

  /**
   * Redirect to Stripe checkout for keywords tracked top-up purchase
   */
  async buyKeywordsTrackedTopUp(keywordsTrackedAmount: 25 | 100 | 250): Promise<boolean> {
    const response = await this.purchaseKeywordsTrackedTopUp({ keywordsTrackedAmount });
    if (response.success && response.data?.url) {
      window.location.href = response.data.url;
      return true;
    }
    return false;
  }

  /**
   * Purchase Extra Project add-on (recurring £6/mo)
   */
  async purchaseProjectAddon(): Promise<ApiResponse<CheckoutSessionResponse>> {
    try {
      return await apiClient.post('/subscriptions/addon-project', {});
    } catch (error) {
      console.error('Error creating project add-on session:', error);
      return {
        success: false,
        error: 'Failed to create project add-on session',
        message: 'Unable to start project add-on purchase'
      };
    }
  }

  /**
   * Redirect to Stripe checkout for Extra Project add-on
   */
  async buyProjectAddon(): Promise<boolean> {
    const response = await this.purchaseProjectAddon();
    if (response.success && response.data?.url) {
      window.location.href = response.data.url;
      return true;
    }
    return false;
  }

  /**
   * Get detailed credit breakdown (GEO, Keywords, AI Tokens)
   */
  async getCredits(): Promise<{
    success: boolean;
    data?: {
      geoCredits: { used: number; limit: number; available: number };
      keywordCredits: { used: number; limit: number; available: number };
      aiTokens: { used: number; limit: number; available: number };
    };
    error?: string;
  }> {
    try {
      const response = await apiClient.get<ApiResponse<any>>('subscriptions/credits');
      if (response.success) {
        return {
          success: true,
          data: response.data
        };
      }
      return { success: false, error: response.error || 'Failed to fetch credits' };
    } catch (error) {
      console.error('Error fetching credits:', error);
      return { success: false, error: 'Failed to fetch credits' };
    }
  }
}

export const subscriptionService = new SubscriptionService();
